import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import distance from '@turf/distance';
import along from '@turf/along';
import { lineString } from '@turf/helpers';
import Button from '../components/Button';
import Sidebar from '../components/Sidebar';
import GearsIcon from '../components/icons/GearsIcon';
import TracesIcon from '../components/icons/TracesIcon';
import { MAPBOX_STYLE } from '../constants/mapbox';
import './GeoJsonGpxPage.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

if (!MAPBOX_TOKEN) {
  console.error('Missing VITE_MAPBOX_TOKEN. Please check your .env.local file.');
}

const GeoJsonGpxPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const headerRef = useRef(null);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(350);
  const [headerHeight, setHeaderHeight] = useState(50);
  const [skiSpeed, setSkiSpeed] = useState('');
  const [gpsUpdatesPerSecond, setGpsUpdatesPerSecond] = useState('0.5');
  // Traces: array of { id, name, coordinates, visible, isOriginal }
  const [traces, setTraces] = useState([]);
  const [nextTraceId, setNextTraceId] = useState(1);

  // Helper function to extract all coordinates from GeoJSON
  const extractCoordinates = useCallback((geoJson) => {
    let allCoordinates = [];

    if (geoJson.type === 'FeatureCollection' && geoJson.features) {
      geoJson.features.forEach(feature => {
        if (feature.geometry && feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates;
          if (feature.geometry.type === 'Point') {
            allCoordinates.push(coords);
          } else if (feature.geometry.type === 'LineString') {
            allCoordinates.push(...coords);
          } else if (feature.geometry.type === 'MultiLineString') {
            coords.forEach(line => {
              allCoordinates.push(...line);
            });
          } else if (feature.geometry.type === 'Polygon') {
            if (coords[0] && Array.isArray(coords[0])) {
              allCoordinates.push(...coords[0]);
            }
          }
        }
      });
    } else if (geoJson.type === 'Feature' && geoJson.geometry) {
      const coords = geoJson.geometry.coordinates;
      if (geoJson.geometry.type === 'Point') {
        allCoordinates.push(coords);
      } else if (geoJson.geometry.type === 'LineString') {
        allCoordinates.push(...coords);
      } else if (geoJson.geometry.type === 'MultiLineString') {
        coords.forEach(line => {
          allCoordinates.push(...line);
        });
      } else if (geoJson.geometry.type === 'Polygon') {
        if (coords[0] && Array.isArray(coords[0])) {
          allCoordinates.push(...coords[0]);
        }
      }
    }

    return allCoordinates;
  }, []);

  // Get GeoJSON data from location state (passed from upload modal)
  useEffect(() => {
    if (location.state && location.state.geoJsonData) {
      const data = location.state.geoJsonData;
      setGeoJsonData(data);
      
      // Add original GeoJSON as a trace
      const originalCoords = extractCoordinates(data);
      if (originalCoords.length > 0) {
        setTraces([{
          id: 'original',
          name: 'Original GeoJSON',
          coordinates: originalCoords,
          visible: true,
          isOriginal: true,
          color: '#9D4EDD' // Purple color for original
        }]);
      }
    } else {
      // If no data, redirect back to tools page
      navigate('/tools');
    }
  }, [location.state, navigate, extractCoordinates]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !MAPBOX_TOKEN || mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Initialize Mapbox map
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAPBOX_STYLE,
      center: [-106.3550, 39.6061], // Default center (Vail, Colorado)
      zoom: 13,
      pitch: 60,
      bearing: 0
    });

    mapRef.current = map;

    // Resize map after it loads
    const resizeMap = () => {
      if (mapRef.current && mapRef.current.loaded()) {
        mapRef.current.resize();
      }
    };

    if (map.loaded()) {
      resizeMap();
    } else {
      map.once('load', resizeMap);
    }

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Display all traces on map
  useEffect(() => {
    if (!mapRef.current || traces.length === 0) {
      return;
    }

    const map = mapRef.current;
    
    // Function to render traces
    const renderTraces = () => {
      if (!map.loaded()) {
        return;
      }

      // Remove all existing trace layers, sources, and event listeners
      traces.forEach(trace => {
        const layerIds = [
          `trace-${trace.id}-path`,
          `trace-${trace.id}-points`,
          `trace-${trace.id}-labels`
        ];
        
        // Remove event listeners before removing layers
        layerIds.forEach(layerId => {
          if (map.getLayer(layerId)) {
            // Remove click and mouseenter/mouseleave handlers
            map.off('click', layerId);
            map.off('mouseenter', layerId);
            map.off('mouseleave', layerId);
            map.removeLayer(layerId);
          }
        });
        
        const sourceIds = [`trace-${trace.id}-source`, `trace-${trace.id}-points-source`];
        sourceIds.forEach(sourceId => {
          if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        });
      });
      
      // Remove any existing popups
      const popups = document.getElementsByClassName('mapboxgl-popup');
      while (popups.length > 0) {
        popups[0].remove();
      }

    // Add all visible traces
    traces.forEach(trace => {
      if (!trace.visible || !trace.coordinates || trace.coordinates.length === 0) {
        return;
      }

      const isOriginal = trace.isOriginal || false;
      const lineColor = isOriginal ? '#9D4EDD' : (trace.color || '#22c55e'); // Purple for original, random color for generated

      // Create LineString feature
      const lineStringFeature = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: trace.coordinates
        },
        properties: {}
      };

      // Create point features with numbers and point index
      const pointFeatures = trace.coordinates.map((coord, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coord
        },
        properties: {
          pointNumber: index + 1,
          pointIndex: index,
          traceId: trace.id,
          isOriginal: trace.isOriginal || false,
          startTime: trace.startTime || null,
          gpsUpdatesPerSecond: trace.gpsUpdatesPerSecond || null
        }
      }));

      // Add sources
      map.addSource(`trace-${trace.id}-source`, {
        type: 'geojson',
        data: lineStringFeature
      });

      map.addSource(`trace-${trace.id}-points-source`, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: pointFeatures
        }
      });

      // Add line layer (dotted for original, solid for generated)
      map.addLayer({
        id: `trace-${trace.id}-path`,
        type: 'line',
        source: `trace-${trace.id}-source`,
        paint: {
          'line-color': lineColor,
          'line-width': 3,
          'line-opacity': 0.9,
          ...(isOriginal ? { 'line-dasharray': [2, 2] } : {})
        }
      });

      // Add points layer
      map.addLayer({
        id: `trace-${trace.id}-points`,
        type: 'circle',
        source: `trace-${trace.id}-points-source`,
        paint: {
          'circle-radius': 6,
          'circle-color': lineColor,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Add labels
      map.addLayer({
        id: `trace-${trace.id}-labels`,
        type: 'symbol',
        source: `trace-${trace.id}-points-source`,
        layout: {
          'text-field': ['get', 'pointNumber'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, 1.25],
          'text-anchor': 'top'
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 2
        }
      });

      // Add click handlers for generated traces (not original)
      if (!isOriginal && trace.startTime && trace.gpsUpdatesPerSecond) {
        // Add click handler to points layer
        map.on('click', `trace-${trace.id}-points`, (e) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: [`trace-${trace.id}-points`]
          });

          if (features.length > 0) {
            const feature = features[0];
            const props = feature.properties;
            
            if (props.startTime && props.gpsUpdatesPerSecond) {
              // Calculate timestamp for this point
              const timeBetweenPoints = 1 / props.gpsUpdatesPerSecond; // seconds
              const pointTimestamp = props.startTime + (props.pointIndex * timeBetweenPoints * 1000); // milliseconds
              const date = new Date(pointTimestamp);
              
              // Format timestamp
              const timestampStr = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
              });

              // Create popup
              new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setHTML(`
                  <div style="padding: 4px;">
                    <div style="font-weight: 600; margin-bottom: 4px;">Point ${props.pointNumber}</div>
                    <div style="font-size: 12px; color: #cccccc;">${timestampStr}</div>
                  </div>
                `)
                .addTo(map);
            }
          }
        });

        // Change cursor to pointer on hover
        map.on('mouseenter', `trace-${trace.id}-points`, () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', `trace-${trace.id}-points`, () => {
          map.getCanvas().style.cursor = '';
        });
      }
    });

    // Fit bounds to all visible traces
    const bounds = new mapboxgl.LngLatBounds();
    traces.forEach(trace => {
      if (trace.visible && trace.coordinates) {
        trace.coordinates.forEach(coord => {
          if (coord && Array.isArray(coord) && coord.length >= 2) {
            bounds.extend(coord);
          }
        });
      }
    });

    if (!bounds.isEmpty()) {
      try {
        map.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
          duration: 1000
        });
      } catch (err) {
        // Silently fail if bounds fitting fails
      }
    }
    };
    
    // Wait for map to load if it hasn't already
    if (!map.loaded()) {
      const loadHandler = () => {
        renderTraces();
      };
      map.once('load', loadHandler);
      return () => {
        map.off('load', loadHandler);
      };
    } else {
      // Map is already loaded, render immediately
      renderTraces();
    }
  }, [traces]);

  // Resize map when container size changes
  useEffect(() => {
    if (!mapRef.current) return;

    let rafId = null;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        if (mapRef.current && mapRef.current.loaded()) {
          mapRef.current.resize();
        }
        rafId = null;
      });
    });

    if (mapContainer.current) {
      resizeObserver.observe(mapContainer.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Measure header height for sidebar positioning
  useEffect(() => {
    const measureHeader = () => {
      if (headerRef.current) {
        const bottom = headerRef.current.getBoundingClientRect().bottom;
        setHeaderHeight(bottom);
      }
    };

    measureHeader();

    const timeoutId = setTimeout(measureHeader, 100);

    let resizeObserver = null;
    let rafId = null;
    const currentHeaderRef = headerRef.current;
    if (currentHeaderRef && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          measureHeader();
        });
      });
      resizeObserver.observe(currentHeaderRef);
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver && currentHeaderRef) {
        resizeObserver.unobserve(currentHeaderRef);
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Function to generate interpolated coordinates
  const generateInterpolatedCoordinates = useCallback((coordinates, speedMph, gpsUpdatesPerSec) => {
    if (!coordinates || coordinates.length < 2) {
      return [];
    }

    // Convert speed from mph to meters per second
    // 1 mph = 1609.34 meters / 3600 seconds = 0.44704 m/s
    const speedMps = speedMph * 0.44704;

    // Calculate time between GPS updates in seconds
    const timeBetweenUpdates = 1 / gpsUpdatesPerSec;

    // Calculate distance between GPS points in meters
    const distanceBetweenPoints = speedMps * timeBetweenUpdates;

    // Create a LineString from the coordinates for turf calculations
    const line = lineString(coordinates);

    // Calculate total distance along the path in kilometers
    let totalDistance = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
      totalDistance += distance(coordinates[i], coordinates[i + 1], { units: 'kilometers' });
    }

    // Convert to meters
    const totalDistanceMeters = totalDistance * 1000;

    // Calculate number of interpolated points needed
    const numPoints = Math.floor(totalDistanceMeters / distanceBetweenPoints) + 1;

    // Generate interpolated points along the path
    const interpolated = [];
    for (let i = 0; i < numPoints; i++) {
      const distanceAlongPath = i * distanceBetweenPoints; // in meters
      const distanceInKm = distanceAlongPath / 1000; // convert to kilometers for turf

      // Get point at this distance along the line
      const point = along(line, distanceInKm, { units: 'kilometers' });
      if (point && point.geometry && point.geometry.coordinates) {
        interpolated.push(point.geometry.coordinates);
      }
    }

    // Always include the last point
    if (coordinates.length > 0) {
      const lastCoord = coordinates[coordinates.length - 1];
      if (interpolated.length === 0 || 
          (interpolated[interpolated.length - 1][0] !== lastCoord[0] || 
           interpolated[interpolated.length - 1][1] !== lastCoord[1])) {
        interpolated.push([...lastCoord]);
      }
    }

    return interpolated;
  }, []);

  // Function to generate a random color (bright, visible colors)
  const generateRandomColor = useCallback(() => {
    // Generate bright, vibrant colors (avoid too dark or too light)
    const hue = Math.floor(Math.random() * 360);
    const saturation = 70 + Math.floor(Math.random() * 30); // 70-100%
    const lightness = 50 + Math.floor(Math.random() * 20); // 50-70%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }, []);

  // Function to handle GPX generation (actually generates interpolated coordinates)
  const handleGenerateGPX = useCallback(() => {
    if (!geoJsonData || !skiSpeed || !gpsUpdatesPerSecond) {
      return;
    }

    const speed = parseFloat(skiSpeed);
    const gpsRate = parseFloat(gpsUpdatesPerSecond);

    if (speed <= 0 || gpsRate <= 0) {
      return;
    }

    // Extract original coordinates
    const originalCoords = extractCoordinates(geoJsonData);

    // Generate interpolated coordinates
    const interpolated = generateInterpolatedCoordinates(originalCoords, speed, gpsRate);
    
    // Add new trace
    const traceName = `${speed} MPH | ${gpsRate} updates per second`;
    const newTrace = {
      id: `trace-${nextTraceId}`,
      name: traceName,
      coordinates: interpolated,
      visible: true,
      isOriginal: false,
      startTime: Date.now(), // Current time in milliseconds
      gpsUpdatesPerSecond: gpsRate,
      color: generateRandomColor() // Random color for this trace
    };
    
    setTraces(prev => [...prev, newTrace]);
    setNextTraceId(prev => prev + 1);
  }, [geoJsonData, skiSpeed, gpsUpdatesPerSecond, extractCoordinates, generateInterpolatedCoordinates, nextTraceId, generateRandomColor]);

  // Function to toggle trace visibility
  const toggleTraceVisibility = useCallback((traceId) => {
    setTraces(prev => prev.map(trace => 
      trace.id === traceId ? { ...trace, visible: !trace.visible } : trace
    ));
  }, []);

  // Function to convert trace to GPX format (Xcode compatible waypoint format)
  const convertTraceToGPX = useCallback((trace) => {
    if (!trace.coordinates || trace.coordinates.length === 0) {
      return null;
    }

    // Format timestamp for GPX (ISO 8601 format without milliseconds)
    const formatGPXTime = (timestamp) => {
      const date = new Date(timestamp);
      return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
    };

    // Build GPX content with Xcode-compatible format
    let gpxContent = `<?xml version="1.0"?>
<gpx version="1.1" creator="Xcode">
`;

    // Add waypoints
    trace.coordinates.forEach((coord, index) => {
      const [lon, lat] = coord;
      
      // Calculate timestamp for this point
      let timeStr = '';
      if (trace.startTime && trace.gpsUpdatesPerSecond) {
        const timeBetweenPoints = 1 / trace.gpsUpdatesPerSecond; // seconds
        const pointTimestamp = trace.startTime + (index * timeBetweenPoints * 1000); // milliseconds
        timeStr = `
        <time>${formatGPXTime(pointTimestamp)}</time>`;
      }
      
      gpxContent += `    <wpt lat="${lat}" lon="${lon}">${timeStr}
    </wpt>
`;
    });

    gpxContent += `</gpx>`;

    return gpxContent;
  }, []);

  // Function to download trace as GPX
  const handleDownloadGPX = useCallback((trace) => {
    const gpxContent = convertTraceToGPX(trace);
    if (!gpxContent) {
      return;
    }

    // Create blob and trigger download
    const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Create filename from trace name (sanitize for filename)
    const filename = `${trace.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.gpx`;
    a.download = filename;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [convertTraceToGPX]);

  // Helper function to format duration as "X hours & Y minutes"
  const formatDuration = useCallback((durationSeconds) => {
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    
    const parts = [];
    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
    
    if (parts.length === 0) {
      return 'Less than 1 minute';
    }
    
    return parts.join(' & ');
  }, []);

  // Helper function to format start time
  const formatStartTime = useCallback((timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  // Define sidebar tabs
  const sidebarTabs = [
    {
      id: 'settings',
      label: 'Settings',
      icon: <GearsIcon size={24} />
    },
    {
      id: 'traces',
      label: 'Traces',
      icon: <TracesIcon size={24} />
    }
  ];

  return (
    <div className="geojson-gpx-page">
      <header ref={headerRef} className="geojson-gpx-header">
        <div className="geojson-gpx-header-content">
          <div className="header-left">
            <Button
              variant="outline"
              size="medium"
              onClick={() => navigate('/tools')}
              className="back-button"
            >
              ←
            </Button>
            <h1>GeoJSON &lt;&gt; GPX Generator</h1>
          </div>
        </div>
      </header>
      <main className="geojson-gpx-main">
        <div className="geojson-gpx-map-container">
          <div ref={mapContainer} className="mapbox-container geojson-gpx-map" />
        </div>
        <Sidebar
          tabs={sidebarTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          width={sidebarWidth}
          onWidthChange={setSidebarWidth}
          topOffset={headerHeight}
        >
          {activeTab === 'settings' && (
            <div className="geojson-settings-panel">
              <h2>Settings</h2>
              <div className="settings-cards">
                <div className="settings-card">
                  <label htmlFor="ski-speed" className="settings-card-label">
                    Choose ski speed
                  </label>
                  <div className="settings-card-input-wrapper">
                    <input
                      id="ski-speed"
                      type="number"
                      className="settings-card-input"
                      value={skiSpeed}
                      onChange={(e) => setSkiSpeed(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.1"
                    />
                    <span className="settings-card-unit">mph</span>
                  </div>
                </div>
                <div className="settings-card">
                  <label htmlFor="gps-updates" className="settings-card-label">
                    # of GPS updates per second
                  </label>
                  <div className="settings-card-input-wrapper">
                    <input
                      id="gps-updates"
                      type="number"
                      className="settings-card-input"
                      value={gpsUpdatesPerSecond}
                      onChange={(e) => setGpsUpdatesPerSecond(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
              <div className="settings-actions">
                <Button
                  variant="success"
                  size="medium"
                  onClick={handleGenerateGPX}
                  disabled={!skiSpeed || !gpsUpdatesPerSecond || parseFloat(skiSpeed) <= 0 || parseFloat(gpsUpdatesPerSecond) <= 0}
                >
                  Generate GPX file
                </Button>
              </div>
            </div>
          )}
          {activeTab === 'traces' && (
            <div className="geojson-traces-panel">
              <h2>Traces</h2>
              <div className="traces-list">
                {traces.map(trace => {
                  // Calculate duration for non-original traces
                  let durationText = null;
                  let startTimeText = null;
                  const numPoints = trace.coordinates ? trace.coordinates.length : 0;
                  
                  if (!trace.isOriginal && trace.startTime && trace.gpsUpdatesPerSecond && trace.coordinates) {
                    const timeBetweenPoints = 1 / trace.gpsUpdatesPerSecond; // seconds
                    const durationSeconds = (numPoints - 1) * timeBetweenPoints;
                    durationText = formatDuration(durationSeconds);
                    startTimeText = formatStartTime(trace.startTime);
                  }
                  
                  return (
                    <div key={trace.id} className="trace-card">
                      <div className="trace-card-header">
                        <div className="trace-card-info">
                          <div className="trace-card-name-row">
                            {trace.color && (
                              <div 
                                className="trace-card-color-indicator"
                                style={{ backgroundColor: trace.color }}
                              />
                            )}
                            <span className="trace-card-name">{trace.name}</span>
                          </div>
                          <div className="trace-card-metadata">
                            <span className="trace-card-points">Points: {numPoints}</span>
                            {startTimeText && durationText && (
                              <>
                                <span className="trace-card-time">Start: {startTimeText}</span>
                                <span className="trace-card-duration">Duration: {durationText}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="trace-card-actions">
                          {!trace.isOriginal && (
                            <button
                              className="trace-card-download-button"
                              onClick={() => handleDownloadGPX(trace)}
                              title="Download GPX"
                              aria-label="Download GPX"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                            </button>
                          )}
                          <Button
                            variant={trace.visible ? 'secondary' : 'outline'}
                            size="small"
                            onClick={() => toggleTraceVisibility(trace.id)}
                          >
                            {trace.visible ? 'Hide' : 'Show'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Sidebar>
      </main>
    </div>
  );
};

export default GeoJsonGpxPage;

